import { useEffect } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";

import {
  MAX_MEMORY_CHARS,
  THREAD_MEMORY_CAP,
  USER_MEMORY_CAP,
} from "utilities/memory";
import modalStyles from "../Modal/Modal.module.scss";
import styles from "./MemoryView.module.scss";

interface MemoryInfoModalProps {
  onClose: () => void;
}

/** Explains what memory is and how the memory view works (UI ADR 078). */
export default function MemoryInfoModal({ onClose }: MemoryInfoModalProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const modalContent = (
    <div className={modalStyles.backdrop} onClick={handleBackdropClick}>
      <div
        className={modalStyles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="memory-info-title"
      >
        <div className={modalStyles.header}>
          <h2
            id="memory-info-title"
            className={`${modalStyles.sectionTitle} ${styles.infoTitle}`}
          >
            <FontAwesomeIcon icon={faCircleInfo} className={styles.infoIcon} />
            What is Memory?
          </h2>
          <button
            className={modalStyles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className={modalStyles.content}>
          <div className={modalStyles.section}>
            <p className={modalStyles.sectionDescription}>
              Memories are short facts that the assistant reads at the start of
              every message, so you do not have to repeat them. Each memory can
              be up to {MAX_MEMORY_CHARS} characters.
            </p>
            <p className={modalStyles.sectionDescription}>
              The assistant saves a memory when you share something worth
              keeping, such as a preference or the project you work on. Those
              memories show a &ldquo;Saved by assistant&rdquo; tag. You can also
              add, edit, or delete any memory here.
            </p>
            <p className={modalStyles.sectionDescription}>
              Memories are background information. What you say in a chat takes
              priority over them.
            </p>
          </div>

          <div className={modalStyles.section}>
            <h3 className={modalStyles.sectionTitle}>Two kinds of memory</h3>
            <p className={modalStyles.sectionDescription}>
              <strong>About you:</strong> used in all of your chats. Up to{" "}
              {USER_MEMORY_CAP} memories.
            </p>
            <p className={modalStyles.sectionDescription}>
              <strong>This chat:</strong> used only in the open chat. Up to{" "}
              {THREAD_MEMORY_CAP} memories per chat.
            </p>
          </div>

          <div className={modalStyles.section}>
            <h3 className={modalStyles.sectionTitle}>Turning memory off</h3>
            <p className={modalStyles.sectionDescription}>
              When &ldquo;Use memory&rdquo; is off, the assistant does not read
              or save memories. Your saved memories stay, and you can still edit
              them. The setting applies only to this browser. Incognito chats
              never use memory.
            </p>
          </div>
        </div>

        <div className={modalStyles.footer}>
          <button className={modalStyles.secondaryButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
